import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { type Schema as PMSchema, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType.ts';
import type { StepsDatabase } from '../stepsDatabase.ts';

import { applyStepsCommand, noStepsCommand, setInactiveSyncingCommand } from './commands.ts';

const MAX_APPLY_ATTEMPTS = 3;

export type Status = 'init' | 'leader_granted' | 'follower_granted' | 'follower_waiting';

export const nextStatus = (
	status: Status,
	granted?: boolean,
): 'leader_granted' | 'follower_granted' | 'follower_waiting' => {
	switch (status) {
		case 'init':
			return granted ? 'leader_granted' : 'follower_waiting'; // First tab becomes leader
		case 'follower_waiting':
			return granted ? 'follower_granted' : 'follower_waiting';
		case 'leader_granted':
		case 'follower_granted':
			return status;
	}
};

export type RestoreOptions = {
	createOfflineVersion?: (props: { doc: PMNode; steps: ProseMirrorStep[] }) => void;
};

const recreateOfflineDoc = (jsonDoc: JSONDocNode, steps: ProseMirrorStep[], pmSchema: PMSchema) => {
	try {
		let doc = pmSchema.nodeFromJSON(jsonDoc);
		steps.forEach((step) => {
			const res = step.apply(doc);
			if (!res.failed && res.doc) {
				doc = res.doc;
			}
		});
		return doc;
	} catch {
		// TODO: Follow-up error handling
		return undefined;
	}
};

/**
	Syncs the offline steps to the editor. It will attempt to apply steps from the offline database.
	If there are no steps, it will check for a version mismatch and sync from the offline database
	@returns true if the sync was successful, false otherwise.
*/
export async function syncOfflineSteps(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	pmSchema: PMSchema,
	options?: RestoreOptions,
): Promise<boolean> {
	try {
		const { steps, docJSON } = await stepsDatabase.getData(pmSchema);

		if (steps.length > 0) {
			if (docJSON) {
				const offlineDoc = recreateOfflineDoc(docJSON, steps, pmSchema);
				if (offlineDoc) {
					options?.createOfflineVersion?.({ doc: offlineDoc, steps });
				}
			}
			const dispatched = api?.core.actions.execute(applyStepsCommand(steps, api));
			if (!dispatched) {
				return false;
			}
		}

		return true;
	} catch {
		return false;
	}
}

/**
 * Fallback scenario when something has gone wrong. Mark the editor as ready with noStepsCommand(),
 * release the sync lock, and set the editor as no longer syncing.
 */
export async function handleFallbackScenario(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
): Promise<void> {
	api?.core.actions.execute(noStepsCommand());
	await stepsDatabase.releaseSyncLock();
	api?.core.actions.execute(setInactiveSyncingCommand(false));
}

/**
 * This is the leader tab. It will attempt to sync the offline steps to the editor from the offline database.
 * It's possible for a late leader to try sync the steps after an earlier leader already synced them.
 * In this case, the version mismatch in syncOfflineSteps will trigger a syncFromAnotherSource.
 * The handler has 3 attempts to sync, with a 1 second delay between attempts.
 */
export async function handleLeaderGrantedStatus(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	pmSchema: PMSchema,
	options?: RestoreOptions,
): Promise<void> {
	let attempts = 0;

	while (attempts < MAX_APPLY_ATTEMPTS) {
		const hasSyncSucceeded = await syncOfflineSteps(api, stepsDatabase, pmSchema, {
			createOfflineVersion: options?.createOfflineVersion,
		});

		if (hasSyncSucceeded) {
			// The leader tab relies on the usePluginHook to release their lock and clear the steps from the DB.
			// We do not set the leader as "syncing", so we do not need to call setInactiveSyncingCommand(false).
			return;
		} else {
			// Allow 1 second between attempts.
			await new Promise((resolve) => setTimeout(resolve, 1000));
			attempts++;
		}
	}

	// If we get here, leader path failed after all attempts.
	await handleFallbackScenario(api, stepsDatabase);
}

/**
 * When the follower succeeds, we complete the sync and get the editor ready for editing.
 */
export async function handleFollowerSuccess(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
): Promise<void> {
	// Wait 2 seconds before allowing the follower to complete, as sometimes it can complete too quickly, making the banner flash briefly.
	await new Promise((resolve) => setTimeout(resolve, 2000));

	api?.core.actions.execute(noStepsCommand());
	await stepsDatabase.releaseSyncLock();
	api?.core.actions.execute(setInactiveSyncingCommand(false));
}

/**
 * This is the follower tab. It will attempt to sync the offline steps to the editor from the offline database.
 * It's likely the steps were already cleared by the leader, so we instead sync with the docJSON from the database.
 * Unlike the leader, on success we immediately release the lock and set the editor as no longer syncing (handleFollowerSuccess).
 * The handler has 3 attempts to sync, with a 1 second delay between attempts. It's possible for the follower to recover from a failed leader.
 */
export async function handleFollowerGrantedStatus(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	pmSchema: PMSchema,
): Promise<void> {
	let attempts = 0;

	while (attempts < MAX_APPLY_ATTEMPTS) {
		const hasSyncSucceeded = await syncOfflineSteps(api, stepsDatabase, pmSchema);

		if (hasSyncSucceeded) {
			await handleFollowerSuccess(api, stepsDatabase);
			return;
		} else {
			// Allow 1 second between attempts.
			await new Promise((resolve) => setTimeout(resolve, 1000));
			attempts++;
		}
	}

	// If we get here, follower path failed after all attempts.
	await handleFallbackScenario(api, stepsDatabase);
}

/**
 * On waiting we acquire a sync lock and wait for the leader to release theirs.
 * If the lock is not released within the timeout, we release the lock and mark the editor as no longer syncing.
 * Until then, the editor is held in a syncing state, and will not allow editing.
 */
export async function handleWaitingStatus(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	lockCallback: (granted?: boolean) => Promise<void>,
): Promise<void> {
	const LOCK_TIMEOUT = 20000; // Wait for the lock, but only up to 20 seconds.

	api?.core.actions.execute(setInactiveSyncingCommand(true));
	// TODO: Remove the timeoutAfterMS param after cleaning up the platform_editor_offline_editing_web_ga_fixes gate. All sync lock acquisitions have an expiration timer now.
	await stepsDatabase.acquireSyncLock(lockCallback, true, 'restore', LOCK_TIMEOUT);
}

/**
 * Creates a callback function that will be called when the lock is acquired or released.
 * It will update the status of the editor and call the appropriate handler function.
 */
export function createLockCallback(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	pmSchema: PMSchema,
	options?: RestoreOptions,
): (granted?: boolean) => Promise<void> {
	let status: Status = 'init';
	let previousStatus: Status = 'init';

	const callback = async (granted?: boolean) => {
		previousStatus = status;
		status = nextStatus(status, granted);

		// If we're transitioning to the same state, we might be in a loop. For example, if the follower is stuck in a loop of follower_waiting -> follower_waiting.
		if (status === previousStatus) {
			await handleFallbackScenario(api, stepsDatabase);
			return;
		}

		switch (status) {
			case 'leader_granted':
				await handleLeaderGrantedStatus(api, stepsDatabase, pmSchema, options);
				break;

			case 'follower_waiting':
				await handleWaitingStatus(api, stepsDatabase, callback);
				break;

			case 'follower_granted':
				await handleFollowerGrantedStatus(api, stepsDatabase, pmSchema);
				break;
		}
	};

	return callback;
}
